import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import Confetti from 'react-confetti';
// Ajout de FaPaperPlane dans l'import ci-dessous
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaExclamationTriangle, FaArrowLeft, FaPaperPlane } from 'react-icons/fa';
import { useColorMode } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

Modal.setAppElement('#root');

export default function Login() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const [email, setEmail] = useState('');
  const [mdp, setMdp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [lockUntil, setLockUntil] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) navigate('/');
  }, [navigate]);

  useEffect(() => {
    let timer;
    if (lockUntil > 0) {
      timer = setInterval(() => {
        setLockUntil((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [lockUntil]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, mdp });
      localStorage.setItem('token', response.data.token);
      const roles = Array.isArray(response.data.admin.roles) ? response.data.admin.roles : ['superadmin'];
      localStorage.setItem('adminRoles', JSON.stringify(roles));
      localStorage.setItem('adminRole', roles[0] || 'superadmin');
      localStorage.setItem('adminNom', response.data.admin.nom || response.data.admin.email);
      localStorage.setItem('adminId', String(response.data.admin.id));
      setModalIsOpen(true);
    } catch (err) {
      if (err.response) {
        if (err.response.status === 429) {
          setLockUntil(err.response.data.lockUntil || 0);
          setError(err.response.data.message || 'Trop de tentatives.');
        } else {
          setError(err.response.data.message || 'Identifiants incorrects.');
        }
        setAttemptsRemaining(err.response.data.attemptsRemaining ?? 5);
      } else {
        setError('Erreur réseau. Vérifiez votre connexion.');
      }
    }
  };

  const inputClass = `w-full pl-10 pr-10 py-3 rounded-xl border transition-all duration-300 outline-none focus:ring-2 focus:ring-yellow-400 ${
    isDark 
      ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400' 
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 shadow-sm'
  }`;

  return (
    <div className={`flex min-h-screen items-center justify-center px-6 py-12 transition-colors duration-500 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`w-full max-w-md p-8 rounded-3xl shadow-2xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}
      >
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-yellow-400 rounded-2xl flex items-center justify-center shadow-lg mb-4 text-[#274559]">
            <FaLock size={30} />
          </div>
          <h2 className={`text-3xl font-black text-center ${isDark ? 'text-white' : 'text-[#274559]'}`}>
            Espace <span className="text-yellow-400">Admin</span>
          </h2>
          <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Connectez-vous pour gérer votre plateforme
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <FaEnvelope className="absolute left-3 top-4 text-gray-400" />
            <input
              type="email"
              required
              placeholder="Adresse e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="relative">
            <FaLock className="absolute left-3 top-4 text-gray-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              required
              placeholder="Mot de passe"
              value={mdp}
              onChange={(e) => setMdp(e.target.value)}
              className={inputClass}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-4 text-gray-400 hover:text-yellow-400 transition-colors"
            >
              {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
            </button>
          </div>

          <AnimatePresence>
            {(error || lockUntil > 0) && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-red-500/10 border border-red-500/50 text-red-500 p-3 rounded-xl text-sm flex items-center gap-2"
              >
                <FaExclamationTriangle className="shrink-0" />
                <span>
                  {lockUntil > 0 ? `Verrouillé encore ${lockUntil}s` : error}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center justify-between px-1">
             <span className={`text-xs font-bold uppercase tracking-widest ${attemptsRemaining <= 2 ? 'text-red-500' : 'text-gray-500'}`}>
               Tentatives : {attemptsRemaining}/10
             </span>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={lockUntil > 0}
            className={`w-full py-4 rounded-2xl font-bold text-lg shadow-xl transition-all duration-300 ${
              lockUntil > 0 
                ? 'bg-gray-600 cursor-not-allowed opacity-50' 
                : 'bg-yellow-400 hover:bg-yellow-500 text-[#274559] shadow-yellow-400/20'
            }`}
          >
            Se connecter
          </motion.button>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => navigate('/demande-acces')}
              className={`w-full flex items-center justify-center gap-2 text-sm font-semibold hover:text-yellow-500 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            >
              Pas encore de compte ? Faire une demande d'accès
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className={`w-full flex items-center justify-center gap-2 text-sm font-semibold hover:text-yellow-500 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            >
              <FaArrowLeft /> Retour au site
            </button>
          </div>
        </form>
      </motion.div>

      <Modal 
        isOpen={modalIsOpen} 
        className="flex items-center justify-center fixed inset-0 z-50 p-4"
        overlayClassName="fixed inset-0 bg-black/80 backdrop-blur-sm"
      >
        <Confetti />
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-10 shadow-2xl max-w-sm w-full text-center relative"
        >
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaPaperPlane size={30} />
          </div>
          <h2 className="text-3xl font-black text-[#274559] mb-2">Succès !</h2>
          <p className="text-gray-600 mb-8">Ravi de vous revoir parmi nous.</p>
          <button 
            onClick={() => navigate('/')} 
            className="w-full bg-[#274559] text-white py-4 rounded-2xl font-bold hover:bg-gray-800 transition-colors shadow-lg"
          >
            Tableau de bord
          </button>
        </motion.div>
      </Modal>
    </div>
  );
}